import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';

export default function StoreHoursModal() {
  // State variables
  const [showModal, setShowModal] = useState(true);
  const [selectedDay, setSelectedDay] = useState('MONDAY');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [workingHours, setWorkingHours] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [nextId, setNextId] = useState(1);

  // Category management
  const categoryTypes = [
    { name: 'BASIC HOURS', plan: 'Basic', cost: 1 },
    { name: 'PREMIUM HOURS', plan: 'Premium', cost: 1000 },
    { name: 'VIP HOURS', plan: 'VIP', cost: 2000 }
  ];

  const [categoryHours, setCategoryHours] = useState([]);
  const [selectedCategoryStartTime, setSelectedCategoryStartTime] = useState('');
  const [selectedCategoryEndTime, setSelectedCategoryEndTime] = useState('');
  const [maxVisitors, setMaxVisitors] = useState('');
  const [pricePerSpot, setPricePerSpot] = useState('');
  const [categoryEditMode, setCategoryEditMode] = useState(false);
  const [categoryEditIndex, setCategoryEditIndex] = useState(null);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  // Helper values
  const [endTimeKey, setEndTimeKey] = useState(0);
  const [categoryEndTimeKey, setCategoryEndTimeKey] = useState(0);

  // Days of the week
  const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];



  // Reset end time whenever start time changes for working hours
  useEffect(() => {
    setSelectedEndTime('');
    setEndTimeKey(prevKey => prevKey + 1);
  }, [selectedStartTime]);

  // Reset end time whenever start time changes for category hours
  useEffect(() => {
    setSelectedCategoryEndTime('');
    setCategoryEndTimeKey(prevKey => prevKey + 1);
  }, [selectedCategoryStartTime]);

  // Handle editing category hours
  useEffect(() => {
    if (categoryEditMode && categoryEditIndex !== null) {
      const hourToEdit = categoryHours[categoryEditIndex];

      // Set form values
      setMaxVisitors(hourToEdit.maxvisitors);
      setPricePerSpot(hourToEdit.price);
      setSelectedCategoryStartTime(hourToEdit.starttime);

      // Set end time after start time to ensure dependencies resolve properly
      setTimeout(() => {
        setSelectedCategoryEndTime(hourToEdit.endtime);
      }, 50);
    }
  }, [categoryEditMode, categoryEditIndex]);


  // Parse time string to extract hour and minute
  const parseTimeString = (timeString) => {
    if (!timeString) return { hour: 0, minute: 0 };
    const [hourStr, minuteStr] = timeString.split(':');
    return {
      hour: parseInt(hourStr, 10),
      minute: parseInt(minuteStr, 10)
    };
  };

  // Get the next day of the week
  const getNextDay = (day) => {
    const index = daysOfWeek.indexOf(day);
    return index < daysOfWeek.length - 1 ? daysOfWeek[index + 1] : daysOfWeek[0];
  };

  // Check if a time is within a given range
  const isTimeWithinRange = (time, rangeStart, rangeEnd) => {
    if (time.hour > rangeStart.hour || (time.hour === rangeStart.hour && time.minute >= rangeStart.minute)) {
      if (time.hour < rangeEnd.hour || (time.hour === rangeEnd.hour && time.minute < rangeEnd.minute)) {
        return true;
      }
    }
    return false;
  };

  // Generate 24-hour time slots with 15-minute intervals
  const generateTimeOptions = (day, startHour = 0, startMinute = 0, hoursToGenerate = 24) => {
    const times = [];
    let currentHour = startHour;
    let currentMinute = startMinute;
    const dayAbbr = day.substring(0, 3);

    for (let i = 0; i < hoursToGenerate * 4; i++) {
      const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

      times.push({
        value: timeValue,
        displayValue: formatTimeForDisplay(timeValue, day),
        hour: currentHour,
        minute: currentMinute,
        day: day
      });

      currentMinute += 15;
      if (currentMinute === 60) {
        currentMinute = 0;
        currentHour++;
      }
    }

    return times;
  };

  // Validate if a new time range would overlap with existing working hours
  const wouldTimeRangeOverlap = (startTime, endTime, excludeId = null) => {
    const start = parseTimeString(startTime);
    const end = parseTimeString(endTime);

    return workingHours.some(slot => {
      if (excludeId === slot.id) return false;

      const slotStart = parseTimeString(slot.starttime);
      const slotEnd = parseTimeString(slot.endtime);

      return (
        (start.hour < slotEnd.hour || (start.hour === slotEnd.hour && start.minute < slotEnd.minute)) &&
        (end.hour > slotStart.hour || (end.hour === slotStart.hour && end.minute > slotStart.minute))
      );
    });
  };

  // Get all start time options for the selected day
  const getStartTimeOptions = () => {
    const dayOptions = generateTimeOptions(selectedDay);

    // Filter out times that would overlap with any existing working hours
    return dayOptions.filter(time => {
      // If in edit mode, don't consider the time slot being edited
      const relevantWorkingHours = editMode
        ? workingHours.filter(hour => hour.id !== editIndex)
        : workingHours;

      // Check for each working hour slot if the current time would create an overlap
      for (const slot of relevantWorkingHours) {
        const slotStartTime = parseTimeString(slot.starttime);
        const slotEndTime = parseTimeString(slot.endtime);

        // Check if this time is already within an existing slot
        if (isTimeWithinRange(time, slotStartTime, slotEndTime)) {
          return false;
        }
      }

      return true;
    });
  };



  // Handle editing working hours
  const handleEditWorkingHours = (id) => {
    const hourToEdit = workingHours.find(hour => hour.id === id);
    if (!hourToEdit) return;

    setSelectedStartTime(hourToEdit.starttime);
    setSelectedEndTime(hourToEdit.endtime);
    setEditMode(true);
    setEditIndex(id);
  };

  // Handle deleting working hours
  const handleDeleteWorkingHours = (id) => {
    // Remove the working hour
    const updatedHours = workingHours.filter(hour => hour.id !== id);
    setWorkingHours(updatedHours);

    // Remove any category hours with this ID
    const updatedCategoryHours = categoryHours.filter(hour => hour.id !== id);
    setCategoryHours(updatedCategoryHours);

    // Reset if in edit mode
    if (editMode && editIndex === id) {
      setEditMode(false);
      setEditIndex(null);
      setSelectedStartTime('');
      setSelectedEndTime('');
    }
  };



  // Handle deleting category hours
  const handleDeleteCategoryHours = (index) => {
    const updatedCategoryHours = categoryHours.filter((_, i) => i !== index);
    setCategoryHours(updatedCategoryHours);

    // Reset if in edit mode
    if (categoryEditMode && categoryEditIndex === index) {
      setCategoryEditMode(false);
      setCategoryEditIndex(null);
      setSelectedCategoryStartTime('');
      setSelectedCategoryEndTime('');
      setMaxVisitors('');
      setPricePerSpot('');
    }
  };



  const formatTimeForDisplay = (timeString, day, isEndTime = false, startTimeDay = null) => {
    if (!timeString) return '';

    const [hourStr, minuteStr] = timeString.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    const amPm = hour < 12 ? 'AM' : 'PM';
    const formattedHour = String(hour12).padStart(2, '0');
    const formattedMinute = String(minute).padStart(2, '0');

    // Determine which day to display for end times
    let displayDay = day;
    if (isEndTime && startTimeDay) {
      // If this is an end time and we have a start time to compare with
      const startDay = startTimeDay;
      const startHour = parseTimeString(startTimeDay.time).hour;

      // If end time is earlier in the day than start time, it's likely the next day
      if (hour < startHour || (hour === startHour && minute < parseTimeString(startTimeDay.time).minute)) {
        displayDay = getNextDay(startDay.day);
      }
    }

    const dayAbbr = displayDay?.substring(0, 3);
    return `${formattedHour}:${formattedMinute} ${amPm} (${dayAbbr})`;
  };

  useEffect(() => {
    if (editMode && editIndex !== null) {
      const hourToEdit = workingHours.find(hour => hour.id === editIndex);
      if (hourToEdit) {
        // First set the start time
        setSelectedStartTime(hourToEdit.starttime);

        // Then set the end time directly, bypassing the reset effect
        setSelectedEndTime(hourToEdit.endtime);
      }
    }
  }, [editMode, editIndex, workingHours]);


  useEffect(() => {
    // Only reset end time when start time changes and NOT in edit mode
    if (!editMode) {
      setSelectedEndTime('');
      setEndTimeKey(prevKey => prevKey + 1);
    }
  }, [selectedStartTime, editMode]);

  // 4. Modify the getEndTimeOptions function to properly handle next day times
  const getEndTimeOptions = () => {
    if (!selectedStartTime) return [];

    const selectedStart = parseTimeString(selectedStartTime);
    const dayOptions = generateTimeOptions(selectedDay);
    const nextDayOptions = generateTimeOptions(getNextDay(selectedDay));

    // Add day information to each option
    const dayOptionsWithDay = dayOptions.map(option => ({
      ...option,
      actualDay: selectedDay
    }));

    const nextDayOptionsWithDay = nextDayOptions.map(option => ({
      ...option,
      actualDay: getNextDay(selectedDay)
    }));

    // Combine current day and next day options
    const allOptions = [...dayOptionsWithDay, ...nextDayOptionsWithDay];

    // Find the index of the selected start time
    const selectedStartIndex = allOptions.findIndex(
      time => time.hour === selectedStart.hour && time.minute === selectedStart.minute && time.actualDay === selectedDay
    );

    if (selectedStartIndex === -1) return [];

    // Get all potential end times (times after the selected start time)
    const potentialEndTimes = allOptions.slice(selectedStartIndex + 1);

    // Filter out times that would overlap with existing working hours
    return potentialEndTimes.filter(endTime => {
      // If in edit mode, don't consider the time slot being edited
      const relevantWorkingHours = editMode
        ? workingHours.filter(hour => hour.id !== editIndex)
        : workingHours;

      // Check for overlap with each existing working hour slot
      for (const slot of relevantWorkingHours) {
        const slotStartTime = parseTimeString(slot.starttime);
        const slotEndTime = parseTimeString(slot.endtime);

        // Handle case where slot end time might be in next day (earlier hours)
        const slotIsOvernight = slotEndTime.hour < slotStartTime.hour ||
          (slotEndTime.hour === slotStartTime.hour && slotEndTime.minute < slotStartTime.minute);

        // Check if this potential end time would overlap with existing slots
        const isCurrentDayEnd = endTime.actualDay === selectedDay;
        const isNextDayEnd = endTime.actualDay === getNextDay(selectedDay);

        // Case 1: Selected start time is before slot's start time
        if (selectedStart.hour < slotStartTime.hour ||
          (selectedStart.hour === slotStartTime.hour && selectedStart.minute < slotStartTime.minute)) {

          // End time must be before or equal to the slot's start time
          if (isCurrentDayEnd && (endTime.hour > slotStartTime.hour ||
            (endTime.hour === slotStartTime.hour && endTime.minute > slotStartTime.minute))) {
            return false;
          }
        }

        // Case 2: Selected start time is within or after the slot
        else if (slotIsOvernight) {
          // For overnight slots, compare with care
          if (isCurrentDayEnd && (slotStartTime.hour <= endTime.hour)) {
            return false;
          }
        } else if (selectedStart.hour < slotEndTime.hour ||
          (selectedStart.hour === slotEndTime.hour && selectedStart.minute < slotEndTime.minute)) {
          // Start time is within the slot, which shouldn't be possible due to getStartTimeOptions filtering
          return false;
        }
      }

      return true;
    });
  };

  // 5. Modify the handleAddWorkingHours function to store day information correctly
  const handleAddWorkingHours = () => {
    if (!selectedStartTime || !selectedEndTime) return;

    // Check for overlaps
    if (wouldTimeRangeOverlap(selectedStartTime, selectedEndTime, editMode ? editIndex : null)) {
      alert("This time range would overlap with existing working hours. Please select a different range.");
      return;
    }

    // Determine if end time is on the next day
    const startTime = parseTimeString(selectedStartTime);
    const endTime = parseTimeString(selectedEndTime);
    const isOvernight = endTime.hour < startTime.hour ||
      (endTime.hour === startTime.hour && endTime.minute < startTime.minute);

    const newWorkingHour = {
      id: editMode ? editIndex : nextId,
      starttime: selectedStartTime,
      endtime: selectedEndTime,
      day: selectedDay,
      endDay: isOvernight ? getNextDay(selectedDay) : selectedDay
    };

    let updatedWorkingHours;

    if (editMode) {
      // Update existing record
      updatedWorkingHours = workingHours.map(hour =>
        hour.id === editIndex ? newWorkingHour : hour
      );

      // Also update any category hours with this ID
      const updatedCategoryHours = categoryHours.map(hour => {
        if (hour.id === editIndex) {
          return {
            ...hour,
            starttime: selectedStartTime,
            endtime: selectedEndTime,
            day: selectedDay,
            endDay: isOvernight ? getNextDay(selectedDay) : selectedDay
          };
        }
        return hour;
      });

      setCategoryHours(updatedCategoryHours);
      setEditMode(false);
      setEditIndex(null);
    } else {
      // Add new record
      updatedWorkingHours = [...workingHours, newWorkingHour];
      setNextId(nextId + 1);

      // Add this time slot to each category with default values
      const newCategoryHours = categoryTypes.map(category => ({
        plan: category.plan,
        price: category.cost,
        maxvisitors: '50', // Default value
        starttime: selectedStartTime,
        endtime: selectedEndTime,
        day: selectedDay,
        endDay: isOvernight ? getNextDay(selectedDay) : selectedDay,
        id: nextId
      }));

      setCategoryHours([...categoryHours, ...newCategoryHours]);
    }

    setWorkingHours(updatedWorkingHours);

    // Reset selections
    setSelectedStartTime('');
    setSelectedEndTime('');
  };


  useEffect(() => {
    if (categoryEditMode && categoryEditIndex !== null) {
      const hourToEdit = categoryHours[categoryEditIndex];

      // Set all form values at once
      setMaxVisitors(hourToEdit.maxvisitors);
      setPricePerSpot(hourToEdit.price);
      setSelectedCategoryStartTime(hourToEdit.starttime);
      setSelectedCategoryEndTime(hourToEdit.endtime);
    }
  }, [categoryEditMode, categoryEditIndex]);

  // 3. Change the useEffect that resets category end time to respect edit mode
  // Replace the current useEffect with this:
  useEffect(() => {
    // Only reset end time when start time changes and NOT in category edit mode
    if (!categoryEditMode) {
      setSelectedCategoryEndTime('');
      setCategoryEndTimeKey(prevKey => prevKey + 1);
    }
  }, [selectedCategoryStartTime, categoryEditMode]);

  // 4. Update the getCategoryEndTimeOptions function to handle overnight times
  // const getCategoryEndTimeOptions = () => {
  //   if (!selectedCategoryStartTime) return [];

  //   // Find the working hour that contains this start time
  //   const workingHour = workingHours.find(hour => {
  //     const startTime = parseTimeString(hour.starttime);
  //     const endTime = parseTimeString(hour.endtime);
  //     const selectedTime = parseTimeString(selectedCategoryStartTime);

  //     return isTimeWithinRange(selectedTime, startTime, endTime) ||
  //       (startTime.hour === selectedTime.hour && startTime.minute === selectedTime.minute);
  //   });

  //   if (!workingHour) return [];

  //   // Generate all time slots from the selected start time to the end of the working hour
  //   const startTime = parseTimeString(selectedCategoryStartTime);
  //   const endTime = parseTimeString(workingHour.endtime);

  //   // Determine if the working hour extends to the next day
  //   const isOvernight = endTime.hour < parseTimeString(workingHour.starttime).hour ||
  //     (endTime.hour === parseTimeString(workingHour.starttime).hour &&
  //       endTime.minute < parseTimeString(workingHour.starttime).minute);

  //   const endTimeOptions = [];
  //   let currentHour = startTime.hour;
  //   let currentMinute = startTime.minute;

  //   // Add 15 minutes to start with the next slot
  //   currentMinute += 15;
  //   if (currentMinute === 60) {
  //     currentMinute = 0;
  //     currentHour++;
  //   }

  //   const maxHour = isOvernight ? 23 : endTime.hour;
  //   const maxMinute = isOvernight ? 45 : endTime.minute;

  //   // Generate all slots until the end of the working hour for current day
  //   while (
  //     currentHour < maxHour ||
  //     (currentHour === maxHour && currentMinute <= maxMinute)
  //   ) {
  //     const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

  //     endTimeOptions.push({
  //       value: timeValue,
  //       displayValue: formatTimeForDisplay(timeValue, selectedDay),
  //       hour: currentHour,
  //       minute: currentMinute,
  //       day: selectedDay,
  //       actualDay: selectedDay
  //     });

  //     currentMinute += 15;
  //     if (currentMinute === 60) {
  //       currentMinute = 0;
  //       currentHour++;
  //     }
  //   }

  //   // If overnight, add the next day options (00:00 to endTime)
  //   if (isOvernight) {
  //     currentHour = 0;
  //     currentMinute = 0;

  //     while (
  //       currentHour < endTime.hour ||
  //       (currentHour === endTime.hour && currentMinute <= endTime.minute)
  //     ) {
  //       const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

  //       endTimeOptions.push({
  //         value: timeValue,
  //         displayValue: formatTimeForDisplay(timeValue, getNextDay(selectedDay)),
  //         hour: currentHour,
  //         minute: currentMinute,
  //         day: getNextDay(selectedDay),
  //         actualDay: getNextDay(selectedDay)
  //       });

  //       currentMinute += 15;
  //       if (currentMinute === 60) {
  //         currentMinute = 0;
  //         currentHour++;
  //       }
  //     }
  //   }

  //   // If in edit mode, make sure the current end time is included
  //   if (categoryEditMode && categoryEditIndex !== null) {
  //     const editingHour = categoryHours[categoryEditIndex];
  //     const endTimeExists = endTimeOptions.some(opt => opt.value === editingHour.endtime);

  //     if (!endTimeExists && editingHour.starttime === selectedCategoryStartTime) {
  //       const { hour, minute } = parseTimeString(editingHour.endtime);
  //       const timeValue = editingHour.endtime;

  //       // Determine the day for this end time
  //       const isEditedHourOvernight = hour < startTime.hour ||
  //         (hour === startTime.hour && minute < startTime.minute);
  //       const endTimeDay = isEditedHourOvernight ? getNextDay(selectedDay) : selectedDay;

  //       endTimeOptions.push({
  //         value: timeValue,
  //         displayValue: formatTimeForDisplay(timeValue, endTimeDay),
  //         hour: hour,
  //         minute: minute,
  //         day: endTimeDay,
  //         actualDay: endTimeDay
  //       });

  //       // Sort by time
  //       endTimeOptions.sort((a, b) => {
  //         if (a.actualDay !== b.actualDay) {
  //           // Different days - next day comes after current day
  //           return a.actualDay === selectedDay ? -1 : 1;
  //         }
  //         if (a.hour !== b.hour) return a.hour - b.hour;
  //         return a.minute - b.minute;
  //       });
  //     }
  //   }

  //   return endTimeOptions;
  // };

  // // 5. Update the handleAddCategoryHours function to handle overnight times
  // const handleAddCategoryHours = () => {
  //   if (!selectedCategoryStartTime || !selectedCategoryEndTime) return;

  //   const categoryPlan = categoryTypes[activeCategoryIndex].plan;

  //   // Determine if end time is on the next day
  //   const startTime = parseTimeString(selectedCategoryStartTime);
  //   const endTime = parseTimeString(selectedCategoryEndTime);
  //   const isOvernight = endTime.hour < startTime.hour ||
  //     (endTime.hour === startTime.hour && endTime.minute < startTime.minute);

  //   // Find the corresponding working hour for this time range
  //   const workingHourId = findWorkingHourIdForTimeRange(selectedCategoryStartTime, selectedCategoryEndTime);

  //   const newCategoryHour = {
  //     plan: categoryPlan,
  //     price: pricePerSpot || categoryTypes[activeCategoryIndex].cost,
  //     maxvisitors: maxVisitors || '50',
  //     starttime: selectedCategoryStartTime,
  //     endtime: selectedCategoryEndTime,
  //     day: selectedDay,
  //     endDay: isOvernight ? getNextDay(selectedDay) : selectedDay,
  //     id: workingHourId
  //   };

  //   if (categoryEditMode) {
  //     // Update existing category hour
  //     const updatedCategoryHours = categoryHours.map((hour, index) =>
  //       index === categoryEditIndex ? newCategoryHour : hour
  //     );

  //     setCategoryHours(updatedCategoryHours);
  //     setCategoryEditMode(false);
  //     setCategoryEditIndex(null);
  //   } else {
  //     // Add new category hour
  //     setCategoryHours([...categoryHours, newCategoryHour]);
  //   }

  //   // Reset selections
  //   setSelectedCategoryStartTime('');
  //   setSelectedCategoryEndTime('');
  //   setMaxVisitors('');
  //   setPricePerSpot('');
  // };


  // 7. Update the findWorkingHourIdForTimeRange function to better handle time ranges
  const findWorkingHourIdForTimeRange = (startTime, endTime) => {
    const start = parseTimeString(startTime);
    const end = parseTimeString(endTime);

    // Check if this is an overnight timespan
    const isOvernight = end.hour < start.hour ||
      (end.hour === start.hour && end.minute < start.minute);

    const hour = workingHours.find(h => {
      const hStart = parseTimeString(h.starttime);
      const hEnd = parseTimeString(h.endtime);

      // Check if the working hour is also overnight
      const hIsOvernight = hEnd.hour < hStart.hour ||
        (hEnd.hour === hStart.hour && hEnd.minute < hStart.minute);

      // For simple same-day comparisons
      if (!isOvernight && !hIsOvernight) {
        return hStart.hour <= start.hour && hEnd.hour >= end.hour &&
          start.hour <= end.hour;
      }

      // For overnight comparisons
      if (isOvernight && hIsOvernight) {
        // Both ranges are overnight, check containment
        return hStart.hour <= start.hour &&
          ((hEnd.hour > end.hour) || (hEnd.hour === end.hour && hEnd.minute >= end.minute));
      }

      // Mixed cases (one overnight, one not) - handle with specific logic
      return h.starttime <= startTime &&
        ((hIsOvernight && end.hour <= 23) || (!hIsOvernight && h.endtime >= endTime));
    });

    return hour ? hour.id : null;
  };




  // Modify the getCategoryStartTimeOptions function to properly handle overnight editing
// const getCategoryStartTimeOptions = () => {
//   // Special case for edit mode - if we're editing, we need to ensure the current start time is available
//   if (categoryEditMode && categoryEditIndex !== null) {
//     const hourToEdit = categoryHours[categoryEditIndex];
//     const categoryPlan = categoryTypes[activeCategoryIndex].plan;
    
//     // If we're editing a record in the currently active category
//     if (hourToEdit.plan === categoryPlan) {
//       // Find the corresponding working hour
//       const correspondingWorkingHour = workingHours.find(hour => hour.id === hourToEdit.id);
      
//       if (correspondingWorkingHour) {
//         // Generate all available time slots from the working hour
//         const startTime = parseTimeString(correspondingWorkingHour.starttime);
//         const endTime = parseTimeString(correspondingWorkingHour.endtime);
        
//         const availableSlots = [];
//         let currentHour = startTime.hour;
//         let currentMinute = startTime.minute;
        
//         // Include the hour being edited regardless of conflicts
//         const editingStartTime = hourToEdit.starttime;
        
//         // Generate all time slots from working hour
//         while (
//           currentHour < endTime.hour || 
//           (currentHour === endTime.hour && currentMinute < endTime.minute) ||
//           // Handle overnight scenario
//           (endTime.hour < startTime.hour)
//         ) {
//           const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
          
//           // Only add slots that don't conflict with other category hours of the same plan
//           // or if it's the slot we're currently editing
//           const isCurrentlyEditedTime = timeValue === editingStartTime;
          
//           const isTimeUsed = categoryHours.some((hour, index) => {
//             // Skip the hour being edited
//             if (index === categoryEditIndex) {
//               return false;
//             }
            
//             // Only check hours with the same plan
//             if (hour.plan !== categoryPlan) {
//               return false;
//             }
            
//             // Check if this time slot is already used
//             const hourStart = parseTimeString(hour.starttime);
//             const hourEnd = parseTimeString(hour.endtime);
            
//             // Handle overnight scenarios
//             const isHourOvernight = hourEnd.hour < hourStart.hour || 
//               (hourEnd.hour === hourStart.hour && hourEnd.minute < hourStart.minute);
              
//             if (hour.starttime === timeValue) {
//               return true;
//             }
            
//             if (isHourOvernight) {
//               // For overnight hours, check if time is between start and midnight or between midnight and end
//               if (
//                 (currentHour >= hourStart.hour && currentHour <= 23) || 
//                 (currentHour >= 0 && currentHour < hourEnd.hour) ||
//                 (currentHour === hourEnd.hour && currentMinute < hourEnd.minute)
//               ) {
//                 return true;
//               }
//             } else {
//               // For same-day hours, simple range check
//               if (
//                 (currentHour > hourStart.hour || (currentHour === hourStart.hour && currentMinute >= hourStart.minute)) &&
//                 (currentHour < hourEnd.hour || (currentHour === hourEnd.hour && currentMinute < hourEnd.minute))
//               ) {
//                 return true;
//               }
//             }
            
//             return false;
//           });
          
//           if (isCurrentlyEditedTime || !isTimeUsed) {
//             availableSlots.push({
//               value: timeValue,
//               displayValue: formatTimeForDisplay(timeValue, selectedDay),
//               hour: currentHour,
//               minute: currentMinute,
//               day: selectedDay
//             });
//           }
          
//           currentMinute += 15;
//           if (currentMinute === 60) {
//             currentMinute = 0;
//             currentHour++;
//             if (currentHour === 24) currentHour = 0; // Handle midnight transition
//           }
          
//           // Break if we've gone full circle for overnight hours
//           if (endTime.hour < startTime.hour && currentHour === startTime.hour && currentMinute === startTime.minute) {
//             break;
//           }
//         }
        
//         // Special case: ensure the editing start time is included
//         const editingStartExists = availableSlots.some(slot => slot.value === editingStartTime);
        
//         if (!editingStartExists) {
//           const { hour, minute } = parseTimeString(editingStartTime);
//           availableSlots.push({
//             value: editingStartTime,
//             displayValue: formatTimeForDisplay(editingStartTime, selectedDay),
//             hour: hour,
//             minute: minute,
//             day: selectedDay
//           });
//         }
        
//         // Sort by time
//         availableSlots.sort((a, b) => {
//           if (a.hour !== b.hour) return a.hour - b.hour;
//           return a.minute - b.minute;
//         });
        
//         return availableSlots;
//       }
//     }
//   }
  
//   // Regular (non-edit) logic for getting available category start times
//   // Get all time slots from working hours
//   const availableSlots = [];

//   workingHours.forEach(slot => {
//     const startTime = parseTimeString(slot.starttime);
//     const endTime = parseTimeString(slot.endtime);
//     let currentHour = startTime.hour;
//     let currentMinute = startTime.minute;

//     // Generate all 15-minute slots between start and end times
//     while (
//       currentHour < endTime.hour ||
//       (currentHour === endTime.hour && currentMinute < endTime.minute) ||
//       // Handle overnight scenario
//       (endTime.hour < startTime.hour && (currentHour <= 23 || currentHour < endTime.hour))
//     ) {
//       const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

//       availableSlots.push({
//         value: timeValue,
//         displayValue: formatTimeForDisplay(timeValue, selectedDay),
//         hour: currentHour,
//         minute: currentMinute,
//         day: selectedDay
//       });

//       currentMinute += 15;
//       if (currentMinute === 60) {
//         currentMinute = 0;
//         currentHour++;
//         if (currentHour === 24) currentHour = 0; // Handle midnight transition
//       }
      
//       // Break loop if we've gone full circle for overnight hours
//       if (endTime.hour < startTime.hour && currentHour === startTime.hour && currentMinute === startTime.minute) {
//         break;
//       }
//     }
//   });

//   // Filter out times that are already used in this category
//   return availableSlots.filter(timeSlot => {
//     // Filter based on the active category
//     const categoryPlan = categoryTypes[activeCategoryIndex].plan;

//     // Check if this time is already used in this category
//     const isTimeUsed = categoryHours.some((hour, index) => {
//       // Skip the hour being edited
//       if (categoryEditMode && index === categoryEditIndex) {
//         return false;
//       }

//       // Only check hours with the same plan
//       if (hour.plan !== categoryPlan) {
//         return false;
//       }

//       // Check if this time slot is the start time of another hour
//       if (hour.starttime === timeSlot.value) {
//         return true;
//       }

//       // Check if this time slot falls within another hour's range
//       const hourStart = parseTimeString(hour.starttime);
//       const hourEnd = parseTimeString(hour.endtime);
      
//       // Handle overnight ranges
//       const isOvernight = hourEnd.hour < hourStart.hour || 
//                         (hourEnd.hour === hourStart.hour && hourEnd.minute < hourStart.minute);
      
//       if (isOvernight) {
//         // For overnight ranges, check if the time is between start and midnight
//         // or between midnight and end time
//         return (timeSlot.hour >= hourStart.hour && timeSlot.hour <= 23) || 
//                (timeSlot.hour >= 0 && timeSlot.hour < hourEnd.hour) ||
//                (timeSlot.hour === hourEnd.hour && timeSlot.minute < hourEnd.minute);
//       } else {
//         // For same-day ranges, simple check
//         return isTimeWithinRange(timeSlot, hourStart, hourEnd);
//       }
//     });

//     return !isTimeUsed;
//   });
// };

// // Modify handleEditCategoryHours to properly set up for editing overnight records
// const handleEditCategoryHours = (index) => {
//   const hourToEdit = categoryHours[index];
  
//   // First, set the active category
//   setActiveCategoryIndex(categoryTypes.findIndex(cat => cat.plan === hourToEdit.plan));
  
//   // Set editing mode
//   setCategoryEditMode(true);
//   setCategoryEditIndex(index);
  
//   // Set form values in one go to avoid race conditions
//   setMaxVisitors(hourToEdit.maxvisitors);
//   setPricePerSpot(hourToEdit.price);
  
//   // Need to set these in the correct order with a small delay to ensure the dropdown populates correctly
//   setSelectedCategoryStartTime(hourToEdit.starttime);
  
//   // Using setTimeout to ensure the start time is processed before setting end time
//   setTimeout(() => {
//     setSelectedCategoryEndTime(hourToEdit.endtime);
//   }, 100);
// };

// Update the useEffect that handles category editing to properly initialize form values
useEffect(() => {
  if (categoryEditMode && categoryEditIndex !== null) {
    const hourToEdit = categoryHours[categoryEditIndex];
    
    // Set all form values
    setMaxVisitors(hourToEdit.maxvisitors);
    setPricePerSpot(hourToEdit.price);
    
    // Make sure we're setting values in correct order with delays to allow dependencies to update
    setSelectedCategoryStartTime(hourToEdit.starttime);
    
    // Need a delay before setting end time so that the dropdown can populate based on start time
    setTimeout(() => {
      setSelectedCategoryEndTime(hourToEdit.endtime);
    }, 100);
  }
}, [categoryEditMode, categoryEditIndex]);

// Modify the useEffect that resets the end time when the start time changes
// to account for edit mode properly
useEffect(() => {
  // Only reset end time when not in edit mode or when the user manually changes the start time
  if (categoryEditMode) {
    // In edit mode, don't reset end time automatically
    // This prevents the selected end time from being cleared during edit initialization
    return;
  }
  
  // For non-edit mode or manual changes, reset the end time
  setSelectedCategoryEndTime('');
  setCategoryEndTimeKey(prevKey => prevKey + 1);
}, [selectedCategoryStartTime]);





// ---------------------------------------------


// 1. First, let's add a validation function to check for overlapping category hours
const wouldCategoryTimeRangeOverlap = (startTime, endTime, excludeIndex = null) => {
  const start = parseTimeString(startTime);
  const end = parseTimeString(endTime);
  const categoryPlan = categoryTypes[activeCategoryIndex].plan;
  
  // Handle overnight scenarios
  const isOvernight = end.hour < start.hour || 
    (end.hour === start.hour && end.minute < start.minute);

  return categoryHours.some((slot, index) => {
    // Skip the hour being edited
    if (excludeIndex === index) return false;
    
    // Only check hours with the same plan
    if (slot.plan !== categoryPlan) return false;

    const slotStart = parseTimeString(slot.starttime);
    const slotEnd = parseTimeString(slot.endtime);
    
    // Check if the slot is overnight
    const slotIsOvernight = slotEnd.hour < slotStart.hour || 
      (slotEnd.hour === slotStart.hour && slotEnd.minute < slotStart.minute);
      
    // Case 1: Both ranges are same-day
    if (!isOvernight && !slotIsOvernight) {
      return (
        (start.hour < slotEnd.hour || (start.hour === slotEnd.hour && start.minute < slotEnd.minute)) &&
        (end.hour > slotStart.hour || (end.hour === slotStart.hour && end.minute > slotStart.minute))
      );
    }
    
    // Case 2: Current selection is overnight but slot is same-day
    if (isOvernight && !slotIsOvernight) {
      // Check if start time is before slot end or if end time is after slot start
      return (
        (start.hour < slotEnd.hour || (start.hour === slotEnd.hour && start.minute < slotEnd.minute)) ||
        (end.hour > slotStart.hour || (end.hour === slotStart.hour && end.minute > slotStart.minute))
      );
    }
    
    // Case 3: Current selection is same-day but slot is overnight
    if (!isOvernight && slotIsOvernight) {
      // Check if any part of the selection is within the overnight slot
      return (
        (start.hour >= slotStart.hour || start.hour <= slotEnd.hour) &&
        (end.hour >= slotStart.hour || end.hour <= slotEnd.hour)
      );
    }
    
    // Case 4: Both are overnight
    // This is complex - they overlap unless one ends before the other starts
    return !(
      (end.hour < slotStart.hour || (end.hour === slotStart.hour && end.minute <= slotStart.minute)) ||
      (slotEnd.hour < start.hour || (slotEnd.hour === start.hour && slotEnd.minute <= start.minute))
    );
  });
};

// 2. Now update the handleAddCategoryHours function to include this validation
const handleAddCategoryHours = () => {
  if (!selectedCategoryStartTime || !selectedCategoryEndTime) return;

  // Add validation to prevent overlapping category hours
  if (wouldCategoryTimeRangeOverlap(
    selectedCategoryStartTime, 
    selectedCategoryEndTime, 
    categoryEditMode ? categoryEditIndex : null
  )) {
    alert("This time range would overlap with existing category hours. Please select a different range.");
    return;
  }

  const categoryPlan = categoryTypes[activeCategoryIndex].plan;

  // Determine if end time is on the next day
  const startTime = parseTimeString(selectedCategoryStartTime);
  const endTime = parseTimeString(selectedCategoryEndTime);
  const isOvernight = endTime.hour < startTime.hour ||
    (endTime.hour === startTime.hour && endTime.minute < startTime.minute);

  // Find the corresponding working hour for this time range
  const workingHourId = findWorkingHourIdForTimeRange(selectedCategoryStartTime, selectedCategoryEndTime);

  const newCategoryHour = {
    plan: categoryPlan,
    price: pricePerSpot || categoryTypes[activeCategoryIndex].cost,
    maxvisitors: maxVisitors || '50',
    starttime: selectedCategoryStartTime,
    endtime: selectedCategoryEndTime,
    day: selectedDay,
    endDay: isOvernight ? getNextDay(selectedDay) : selectedDay,
    id: workingHourId
  };

  if (categoryEditMode) {
    // Update existing category hour
    const updatedCategoryHours = categoryHours.map((hour, index) =>
      index === categoryEditIndex ? newCategoryHour : hour
    );

    setCategoryHours(updatedCategoryHours);
    setCategoryEditMode(false);
    setCategoryEditIndex(null);
  } else {
    // Add new category hour
    setCategoryHours([...categoryHours, newCategoryHour]);
  }

  // Reset selections
  setSelectedCategoryStartTime('');
  setSelectedCategoryEndTime('');
  setMaxVisitors('');
  setPricePerSpot('');
};

// 3. Fix the getCategoryEndTimeOptions function to properly handle overnight editing
const getCategoryEndTimeOptions = () => {
  if (!selectedCategoryStartTime) return [];

  // When editing a record (especially overnight records), we need to handle it specially
  if (categoryEditMode && categoryEditIndex !== null) {
    const editingHour = categoryHours[categoryEditIndex];
    
    // If the selected start time matches the editing hour's start time
    if (editingHour.starttime === selectedCategoryStartTime) {
      // Find the working hour that contains this time range
      const workingHour = workingHours.find(hour => hour.id === editingHour.id);
      
      if (workingHour) {
        const startTime = parseTimeString(selectedCategoryStartTime);
        const workingEndTime = parseTimeString(workingHour.endtime);
        
        // Determine if this is an overnight working hour
        const isOvernight = workingEndTime.hour < parseTimeString(workingHour.starttime).hour ||
          (workingEndTime.hour === parseTimeString(workingHour.starttime).hour &&
            workingEndTime.minute < parseTimeString(workingHour.starttime).minute);
            
        const endTimeOptions = [];
        let currentHour = startTime.hour;
        let currentMinute = startTime.minute;
        
        // Add 15 minutes to start with the next slot
        currentMinute += 15;
        if (currentMinute === 60) {
          currentMinute = 0;
          currentHour++;
          if (currentHour === 24) currentHour = 0; // Handle midnight transition
        }
        
        // Generate time slots within the working hour bounds
        const loopUntil = (hr, min) => {
          if (isOvernight) {
            // For overnight, we need to check both current day and next day times
            return (currentHour < hr || 
                   (currentHour === hr && currentMinute <= min)) ||
                   (currentHour <= 23 && hr < startTime.hour);
          } else {
            return currentHour < hr || 
                   (currentHour === hr && currentMinute <= min);
          }
        };
        
        // Generate all slots until the end of the working hour
        while (loopUntil(workingEndTime.hour, workingEndTime.minute)) {
          const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
          
          // Determine if this time is on the next day
          const isNextDay = isOvernight && 
            (currentHour < startTime.hour || 
            (currentHour === 0 && currentMinute === 0));
            
          const timeDay = isNextDay ? getNextDay(selectedDay) : selectedDay;
          
          endTimeOptions.push({
            value: timeValue,
            displayValue: formatTimeForDisplay(timeValue, timeDay),
            hour: currentHour,
            minute: currentMinute,
            day: timeDay,
            actualDay: timeDay
          });
          
          currentMinute += 15;
          if (currentMinute === 60) {
            currentMinute = 0;
            currentHour++;
            if (currentHour === 24) currentHour = 0; // Handle midnight transition
          }
          
          // Break if we've gone full circle
          if (isOvernight && currentHour === startTime.hour && currentMinute === startTime.minute) {
            break;
          }
        }
        
        // Ensure the current editing end time is included
        const currentEndTimeExists = endTimeOptions.some(opt => opt.value === editingHour.endtime);
        
        if (!currentEndTimeExists) {
          const { hour, minute } = parseTimeString(editingHour.endtime);
          const isEndTimeNextDay = isOvernight && 
            (hour < startTime.hour || (hour === 0 && minute === 0));
          const endTimeDay = isEndTimeNextDay ? getNextDay(selectedDay) : selectedDay;
          
          endTimeOptions.push({
            value: editingHour.endtime,
            displayValue: formatTimeForDisplay(
              editingHour.endtime, 
              endTimeDay
            ),
            hour: hour,
            minute: minute,
            day: endTimeDay,
            actualDay: endTimeDay
          });
        }
        
        // Sort by time, with current day times first, then next day times
        endTimeOptions.sort((a, b) => {
          if (a.actualDay !== b.actualDay) {
            return a.actualDay === selectedDay ? -1 : 1;
          }
          if (a.hour !== b.hour) return a.hour - b.hour;
          return a.minute - b.minute;
        });
        
        return endTimeOptions;
      }
    }
  }
  
  // Standard logic for non-edit mode
  // Find the working hour that contains this start time
  const workingHour = workingHours.find(hour => {
    const startTime = parseTimeString(hour.starttime);
    const endTime = parseTimeString(hour.endtime);
    const selectedTime = parseTimeString(selectedCategoryStartTime);

    return isTimeWithinRange(selectedTime, startTime, endTime) ||
      (startTime.hour === selectedTime.hour && startTime.minute === selectedTime.minute);
  });

  if (!workingHour) return [];

  // Generate all time slots from the selected start time to the end of the working hour
  const startTime = parseTimeString(selectedCategoryStartTime);
  const endTime = parseTimeString(workingHour.endtime);

  // Determine if the working hour extends to the next day
  const isOvernight = endTime.hour < parseTimeString(workingHour.starttime).hour ||
    (endTime.hour === parseTimeString(workingHour.starttime).hour &&
      endTime.minute < parseTimeString(workingHour.starttime).minute);

  const endTimeOptions = [];
  let currentHour = startTime.hour;
  let currentMinute = startTime.minute;

  // Add 15 minutes to start with the next slot
  currentMinute += 15;
  if (currentMinute === 60) {
    currentMinute = 0;
    currentHour++;
    if (currentHour === 24) currentHour = 0; // Handle midnight transition
  }

  // Generate all slots until the end of the working hour
  while (
    (isOvernight && (
      currentHour <= 23 || // Current day slots
      currentHour < endTime.hour || // Next day slots before end time
      (currentHour === endTime.hour && currentMinute <= endTime.minute) // Next day slots at end time
    )) ||
    (!isOvernight && (
      currentHour < endTime.hour || // Normal day slots
      (currentHour === endTime.hour && currentMinute <= endTime.minute) // Normal day slots at end time
    ))
  ) {
    const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    
    // Determine if this time is on the next day
    const isNextDay = isOvernight && 
      (currentHour < startTime.hour || 
      (currentHour === 0 && currentMinute === 0));
    
    const timeDay = isNextDay ? getNextDay(selectedDay) : selectedDay;

    endTimeOptions.push({
      value: timeValue,
      displayValue: formatTimeForDisplay(timeValue, timeDay),
      hour: currentHour,
      minute: currentMinute,
      day: timeDay,
      actualDay: timeDay
    });

    currentMinute += 15;
    if (currentMinute === 60) {
      currentMinute = 0;
      currentHour++;
      if (currentHour === 24) currentHour = 0; // Handle midnight transition
    }
    
    // Break if we've gone full circle in an overnight scenario
    if (isOvernight && currentHour === startTime.hour && currentMinute === startTime.minute) {
      break;
    }
  }

  return endTimeOptions;
};

// 4. Fix handleEditCategoryHours to work better with overnight records
const handleEditCategoryHours = (index) => {
  const hourToEdit = categoryHours[index];
  
  // First, set the active category
  setActiveCategoryIndex(categoryTypes.findIndex(cat => cat.plan === hourToEdit.plan));
  
  // Set editing mode
  setCategoryEditMode(true);
  setCategoryEditIndex(index);
  
  // Set form values in correct sequence
  setMaxVisitors(hourToEdit.maxvisitors);
  setPricePerSpot(hourToEdit.price);
  
  // Need to set start time first, then end time after a delay
  setSelectedCategoryStartTime(hourToEdit.starttime);
  
  // Clear any existing timers
  if (window.editTimeoutId) {
    clearTimeout(window.editTimeoutId);
  }
  
  // Using setTimeout to ensure the start time is processed before setting end time
  window.editTimeoutId = setTimeout(() => {
    setSelectedCategoryEndTime(hourToEdit.endtime);
  }, 200); // Increased delay for more reliable behavior
};

// 5. Add window cleanup in a useEffect
useEffect(() => {
  return () => {
    // Clear any lingering timeouts when component unmounts
    if (window.editTimeoutId) {
      clearTimeout(window.editTimeoutId);
    }
  };
}, []);

// 6. Update the getCategoryStartTimeOptions function to better handle overnight records
const getCategoryStartTimeOptions = () => {
  // Special case for edit mode - if we're editing, we need to ensure the current start time is available
  if (categoryEditMode && categoryEditIndex !== null) {
    const hourToEdit = categoryHours[categoryEditIndex];
    const categoryPlan = categoryTypes[activeCategoryIndex].plan;
    
    // If we're editing a record in the currently active category
    if (hourToEdit.plan === categoryPlan) {
      // Find the corresponding working hour
      const correspondingWorkingHour = workingHours.find(hour => hour.id === hourToEdit.id);
      
      if (correspondingWorkingHour) {
        // Generate all available time slots from the working hour
        const startTime = parseTimeString(correspondingWorkingHour.starttime);
        const endTime = parseTimeString(correspondingWorkingHour.endtime);
        
        const isOvernight = endTime.hour < startTime.hour || 
                           (endTime.hour === startTime.hour && endTime.minute < startTime.minute);
        
        const availableSlots = [];
        let currentHour = startTime.hour;
        let currentMinute = startTime.minute;
        
        // Include the hour being edited regardless of conflicts
        const editingStartTime = hourToEdit.starttime;
        
        // Generate all time slots from working hour range
        const loopUntil = (hr, min) => {
          if (isOvernight) {
            // For overnight working hours
            return currentHour <= 23 || // Current day slots
                   currentHour < hr || // Next day slots before end
                   (currentHour === hr && currentMinute < min); // Next day slots at exact end
          } else {
            return currentHour < hr || 
                   (currentHour === hr && currentMinute < min);
          }
        };
        
        // Generate all valid time slots
        while (loopUntil(endTime.hour, endTime.minute)) {
          const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
          
          // Determine if this is the currently edited time 
          const isCurrentlyEditedTime = timeValue === editingStartTime;
          
          // Check if this time slot conflicts with other category hours of the same plan
          const isTimeUsed = categoryHours.some((hour, index) => {
            // Skip the hour being edited
            if (index === categoryEditIndex) {
              return false;
            }
            
            // Only check hours with the same plan
            if (hour.plan !== categoryPlan) {
              return false;
            }
            
            // Check if this time slot is already the start time of another hour
            if (hour.starttime === timeValue) {
              return true;
            }
            
            // Check if this time slot falls within another hour's range
            const hourStart = parseTimeString(hour.starttime);
            const hourEnd = parseTimeString(hour.endtime);
            
            // Handle overnight scenarios correctly
            const isHourOvernight = hourEnd.hour < hourStart.hour || 
              (hourEnd.hour === hourStart.hour && hourEnd.minute < hourStart.minute);
              
            if (isHourOvernight) {
              // For overnight hours, check if time is within the range
              if (
                // After or at start time on day 1
                (currentHour > hourStart.hour || 
                (currentHour === hourStart.hour && currentMinute >= hourStart.minute)) || 
                // Before or at end time on day 2
                (currentHour < hourEnd.hour || 
                (currentHour === hourEnd.hour && currentMinute <= hourEnd.minute))
              ) {
                return true;
              }
            } else {
              // For same-day hours, check if within range
              if (
                (currentHour > hourStart.hour || 
                (currentHour === hourStart.hour && currentMinute >= hourStart.minute)) &&
                (currentHour < hourEnd.hour || 
                (currentHour === hourEnd.hour && currentMinute < hourEnd.minute))
              ) {
                return true;
              }
            }
            
            return false;
          });
          
          // Include the time slot if it's the currently edited one or not already used
          if (isCurrentlyEditedTime || !isTimeUsed) {
            // Determine if this time is on the next day (for overnight working hours)
            const isNextDay = isOvernight && currentHour < startTime.hour;
            const timeDay = isNextDay ? getNextDay(selectedDay) : selectedDay;
            
            availableSlots.push({
              value: timeValue,
              displayValue: formatTimeForDisplay(timeValue, timeDay),
              hour: currentHour,
              minute: currentMinute,
              day: timeDay
            });
          }
          
          currentMinute += 15;
          if (currentMinute === 60) {
            currentMinute = 0;
            currentHour++;
            if (currentHour === 24) currentHour = 0; // Handle midnight transition
          }
          
          // Break if we've gone full circle for overnight hours
          if (isOvernight && currentHour === startTime.hour && currentMinute === startTime.minute) {
            break;
          }
        }
        
        // Ensure the current editing start time is included
        const editingStartExists = availableSlots.some(slot => slot.value === editingStartTime);
        
        if (!editingStartExists) {
          const { hour, minute } = parseTimeString(editingStartTime);
          
          // Determine if this time is on the next day (for overnight working hours)
          const isNextDay = isOvernight && hour < startTime.hour;
          const timeDay = isNextDay ? getNextDay(selectedDay) : selectedDay;
          
          availableSlots.push({
            value: editingStartTime,
            displayValue: formatTimeForDisplay(editingStartTime, timeDay),
            hour: hour,
            minute: minute,
            day: timeDay
          });
        }
        
        // Sort by time - current day first, then next day
        availableSlots.sort((a, b) => {
          if (a.day !== b.day) {
            return a.day === selectedDay ? -1 : 1;
          }
          if (a.hour !== b.hour) return a.hour - b.hour;
          return a.minute - b.minute;
        });
        
        return availableSlots;
      }
    }
  }
  
  // Regular (non-edit) logic for getting available category start times
  // Get all time slots from working hours
  const availableSlots = [];

  workingHours.forEach(slot => {
    const startTime = parseTimeString(slot.starttime);
    const endTime = parseTimeString(slot.endtime);
    
    // Check if this is an overnight working hour
    const isOvernight = endTime.hour < startTime.hour ||
      (endTime.hour === startTime.hour && endTime.minute < startTime.minute);
    
    let currentHour = startTime.hour;
    let currentMinute = startTime.minute;

    // Function to determine when to stop looping
    const loopUntil = (hr, min) => {
      if (isOvernight) {
        // For overnight working hours
        return currentHour <= 23 || // Current day slots
               currentHour < hr || // Next day slots before end
               (currentHour === hr && currentMinute < min); // Next day slots at exact end
      } else {
        return currentHour < hr || 
               (currentHour === hr && currentMinute < min);
      }
    };

    // Generate all 15-minute slots between start and end times
    while (loopUntil(endTime.hour, endTime.minute)) {
      const timeValue = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
      
      // Determine if this time is on the next day (for overnight working hours)
      const isNextDay = isOvernight && currentHour < startTime.hour;
      const timeDay = isNextDay ? getNextDay(selectedDay) : selectedDay;

      availableSlots.push({
        value: timeValue,
        displayValue: formatTimeForDisplay(timeValue, timeDay),
        hour: currentHour,
        minute: currentMinute,
        day: timeDay,
        actualDay: timeDay
      });

      currentMinute += 15;
      if (currentMinute === 60) {
        currentMinute = 0;
        currentHour++;
        if (currentHour === 24) currentHour = 0; // Handle midnight transition
      }
      
      // Break loop if we've gone full circle for overnight hours
      if (isOvernight && currentHour === startTime.hour && currentMinute === startTime.minute) {
        break;
      }
    }
  });

  // Filter out times that are already used in this category
  return availableSlots.filter(timeSlot => {
    // Filter based on the active category
    const categoryPlan = categoryTypes[activeCategoryIndex].plan;

    // Check if this time is already used in this category
    const isTimeUsed = categoryHours.some((hour, index) => {
      // Skip the hour being edited
      if (categoryEditMode && index === categoryEditIndex) {
        return false;
      }

      // Only check hours with the same plan
      if (hour.plan !== categoryPlan) {
        return false;
      }

      // Check if this time slot is the start time of another hour
      if (hour.starttime === timeSlot.value) {
        return true;
      }

      // Check if this time slot falls within another hour's range
      const hourStart = parseTimeString(hour.starttime);
      const hourEnd = parseTimeString(hour.endtime);
      
      // Handle overnight ranges
      const isOvernight = hourEnd.hour < hourStart.hour || 
                        (hourEnd.hour === hourStart.hour && hourEnd.minute < hourStart.minute);
      
      if (isOvernight) {
        // For overnight ranges, check if the time is between start and midnight
        // or between midnight and end time
        return (
          // After start time on first day
          (timeSlot.hour > hourStart.hour || 
           (timeSlot.hour === hourStart.hour && timeSlot.minute >= hourStart.minute)) ||
          // Before end time on second day
          (timeSlot.hour < hourEnd.hour || 
           (timeSlot.hour === hourEnd.hour && timeSlot.minute < hourEnd.minute))
        );
      } else {
        // For same-day ranges, simple check
        return (
          (timeSlot.hour > hourStart.hour || 
           (timeSlot.hour === hourStart.hour && timeSlot.minute >= hourStart.minute)) &&
          (timeSlot.hour < hourEnd.hour || 
           (timeSlot.hour === hourEnd.hour && timeSlot.minute < hourEnd.minute))
        );
      }
    });

    return !isTimeUsed;
  });
};

  return (
    <div id="layout-wrapper">
      <div className="main-content user-management spots">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row table-data">
              <div className="col-12">
                <div className="card">
                  <Modal className="add_hours" show={showModal} onHide={() => setShowModal(false)}>
                    <div className="modal-header">
                      <div className="add_hours_title">
                        <div className="modal-title h4">ADD HOURS</div>
                        <div className="max_visitor">
                          <div className="max_visitor_status">Max Visitors 20</div>
                          <div className="max_visitor_status">Max Party size 20</div>
                          <div className="max_visitor_status">Max Capacity 100</div>
                        </div>
                      </div>
                      <button type="button" className="close-btn" onClick={() => setShowModal(false)}>
                        <span className="material-icons">close</span>
                      </button>
                    </div>

                    <div className="modal-body">
                      <div className="working_hours premium_hours">
                        <h6>SET STORE WORKING HOURS</h6>
                      </div>

                      <div className="table-responsive">
                        <table className="table align-middle">
                          <thead>
                            <tr>
                              <th width="30%">
                                <div className="form-group">
                                  <label className="form_label" id="day-label">Select Day</label>
                                  <select
                                    className="form-select"
                                    value={selectedDay}
                                    onChange={(e) => {
                                      setSelectedDay(e.target.value);
                                      setSelectedStartTime('');
                                      setSelectedEndTime('');
                                    }}
                                  >
                                    {daysOfWeek.map(day => (
                                      <option key={day} value={day}>{day}</option>
                                    ))}
                                  </select>
                                </div>
                              </th>
                              <th width="30%">
                                <div className="form-group">
                                  <label className="form_label" id="open-label">Opens</label>
                                  <select
                                    className="form-select"
                                    value={selectedStartTime}
                                    onChange={(e) => setSelectedStartTime(e.target.value)}
                                  >
                                    <option value="">Select Open Time</option>
                                    {getStartTimeOptions().map(time => (
                                      <option key={time.value} value={time.value}>
                                        {time.displayValue}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </th>
                              <th width="30%">
                                <div className="form-group">
                                  <label className="form_label" id="close-label">Closes</label>
                                  <select
                                    key={endTimeKey}
                                    className="form-select"
                                    value={selectedEndTime}
                                    onChange={(e) => setSelectedEndTime(e.target.value)}
                                    disabled={!selectedStartTime}
                                  >
                                    <option value="">Select Close Time</option>
                                    {getEndTimeOptions().map(time => (
                                      <option key={time.value} value={time.value}>
                                        {time.displayValue}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </th>
                              <th width="10%" className="right">
                                <button
                                  className="fill-btn-style"
                                  id="save-button"
                                  onClick={handleAddWorkingHours}
                                  disabled={!selectedStartTime || !selectedEndTime}
                                >
                                  <span className="material-symbols-outlined">
                                    {editMode ? 'edit' : 'add_circle'}
                                  </span>
                                  {editMode ? 'update' : 'add'}
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody>

                            {workingHours.map((hour) => (
                              <tr key={hour.id}>
                                <td>{hour.day}</td>
                                <td>{formatTimeForDisplay(hour.starttime, hour.day)}</td>
                                <td>{formatTimeForDisplay(hour.endtime, hour.endDay || hour.day, true, { day: hour.day, time: hour.starttime })}</td>
                                <td className="right">
                                  <button className="btn btn-link p-0 me-2" onClick={() => handleEditWorkingHours(hour.id)}>
                                    <span className="material-symbols-outlined">edit</span>
                                  </button>
                                  <button className="btn btn-link p-0" onClick={() => handleDeleteWorkingHours(hour.id)}>
                                    <span className="material-symbols-outlined">delete</span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {categoryTypes.map((category, categoryIndex) => (
                        <div key={categoryIndex}>
                          <div className="working_hours premium_hours">
                            <h6>{category.name}</h6>
                          </div>
                          <div className="table-responsive">
                            <table className="table align-middle">
                              <thead>
                                <tr>
                                  <th width="25%">
                                    <div className="form-group">
                                      <label className="form_label" id="category-open-label">Opens</label>
                                      <select
                                        className="form-select"
                                        value={categoryIndex === activeCategoryIndex ? selectedCategoryStartTime : ''}
                                        onChange={(e) => {
                                          if (categoryIndex !== activeCategoryIndex) {
                                            setActiveCategoryIndex(categoryIndex);
                                          }
                                          setSelectedCategoryStartTime(e.target.value);
                                          setSelectedCategoryEndTime('');
                                        }}
                                        disabled={categoryIndex !== activeCategoryIndex}
                                      >
                                        <option value="">Select Open Time</option>
                                        {categoryIndex === activeCategoryIndex && getCategoryStartTimeOptions().map(time => (
                                          <option key={time.value} value={time.value}>
                                            {time.displayValue}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </th>
                                  <th width="25%">
                                    <div className="form-group">
                                      <label className="form_label" id="category-close-label">Closes</label>
                                      <select
                                        key={categoryEndTimeKey}
                                        className="form-select"
                                        value={categoryIndex === activeCategoryIndex ? selectedCategoryEndTime : ''}
                                        onChange={(e) => setSelectedCategoryEndTime(e.target.value)}
                                        disabled={!selectedCategoryStartTime || categoryIndex !== activeCategoryIndex}
                                      >
                                        <option value="">Select Close Time</option>
                                        {categoryIndex === activeCategoryIndex && selectedCategoryStartTime && getCategoryEndTimeOptions().map(time => (
                                          <option key={time.value} value={time.value}>
                                            {time.displayValue}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </th>
                                  <th width="20%">
                                    <div className="form-group">
                                      <label className="form_label" id="max-visitors-label">Max Visitors</label>
                                      <input
                                        className="form-control"
                                        placeholder="Enter"
                                        type="text"
                                        value={categoryIndex === activeCategoryIndex ? maxVisitors : ''}
                                        onChange={(e) => setMaxVisitors(e.target.value)}
                                        disabled={categoryIndex !== activeCategoryIndex}
                                      />
                                    </div>
                                  </th>
                                  <th width="20%">
                                    <div className="form-group">
                                      <label className="form_label" id="price-label">Price/ Spot</label>
                                      <input
                                        className="form-control"
                                        placeholder="00.00"
                                        type="text"
                                        value={categoryIndex === activeCategoryIndex ? pricePerSpot : ''}
                                        onChange={(e) => setPricePerSpot(e.target.value)}
                                        disabled={categoryIndex !== activeCategoryIndex}
                                      />
                                    </div>
                                  </th>
                                  <th width="10%" className="right">
                                    <button
                                      className="fill-btn-style"
                                      id="save-button"
                                      onClick={handleAddCategoryHours}
                                      disabled={!selectedCategoryStartTime || !selectedCategoryEndTime || categoryIndex !== activeCategoryIndex}
                                    >
                                      <span className="material-symbols-outlined">
                                        {categoryEditMode && categoryIndex === activeCategoryIndex ? 'edit' : 'add_circle'}
                                      </span>
                                      {categoryEditMode && categoryIndex === activeCategoryIndex ? 'update' : 'add'}
                                    </button>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>

                                {categoryHours
                                  .filter(hour => hour.plan === category.plan)
                                  .map((hour, idx) => (
                                    <tr key={`${hour.id}-${idx}`}>
                                      <td>{formatTimeForDisplay(hour.starttime, hour.day)}</td>
                                      <td>{formatTimeForDisplay(hour.endtime, hour.endDay || hour.day, true, { day: hour.day, time: hour.starttime })}</td>
                                      <td>{hour.maxvisitors}</td>
                                      <td>${hour.price}</td>
                                      <td className="right">
                                        <button className="btn btn-link p-0 me-2" onClick={() => handleEditCategoryHours(
                                          categoryHours.findIndex(h => h.id === hour.id && h.plan === hour.plan)
                                        )}>
                                          <span className="material-symbols-outlined">edit</span>
                                        </button>
                                        <button className="btn btn-link p-0" onClick={() => handleDeleteCategoryHours(
                                          categoryHours.findIndex(h => h.id === hour.id && h.plan === hour.plan)
                                        )}>
                                          <span className="material-symbols-outlined">delete</span>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>


                  </Modal>

                </div>


              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};
